/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import { bSGetCollectionPointsAsync } from '../custom-proxy/DataActionExtensions.g';
import { ICollectionPoint, ICollectionPoints, IOpeningHour } from '../custom-proxy/DataServiceEntities.g';
import { IFullOrgUnitAvailability } from '@msdyn365-commerce-modules/retail-actions';
import { OrgUnitAvailability, OrgUnitLocation } from '@msdyn365-commerce/retail-proxy';

export interface IFullOrgUnitAvailabilityExtended extends Omit<IFullOrgUnitAvailability, 'OrgUnitAvailability'> {
    openingHours?: IOpeningHour[];
    OrgUnitAvailability?: OrgUnitAvailabilityExtended;
}

export interface OrgUnitAvailabilityExtended extends Omit<OrgUnitAvailability, 'OrgUnitLocation'> {
    OrgUnitLocation?: OrgUnitLocationExtended;
}

export interface OrgUnitLocationExtended extends OrgUnitLocation {
    ExternalId?: string;
}

/**
 * GetStoreLocationCentiro Input Action
 */
export class GetStoreLocationCentiroInput implements Msdyn365.IActionInput {
    public CountryISOCode?: string;
    public City?: string;
    public PostalCode?: string;
    public Networks?: string;
    public StreetAddress?: string;
    public State?: string;
    public MaxDistance?: string;
    public MaxCollectionPoints?: string;

    constructor(
        CountryISOCode?: string,
        City?: string,
        PostalCode?: string,
        Networks?: string,
        StreetAddress?: string,
        State?: string,
        MaxDistance?: string,
        MaxCollectionPoints?: string
    ) {
        this.CountryISOCode = CountryISOCode;
        this.City = City;
        this.PostalCode = PostalCode;
        this.Networks = Networks;
        this.StreetAddress = StreetAddress;
        this.State = State;
        this.MaxDistance = MaxDistance;
        this.MaxCollectionPoints = MaxCollectionPoints;
    }
    public getCacheKey = () => `GetStoreLocationCentiro`;
    public getCacheObjectType = () => 'GetStoreLocationCentiro';
    public dataCacheType = (): Msdyn365.CacheType => 'none';
}

// TODO: Create a data model here or import one to capture the response of the action
export interface IGetStoreLocationCentiroData {
    collectionPoints: ICollectionPoint[];
}

/**
 * TODO: Use this function to create the input required to make the action call
 */
const createInput = (args: Msdyn365.ICreateActionContext): Msdyn365.IActionInput => {
    return new GetStoreLocationCentiroInput();
};

/**
 * TODO: Use this function to call your action and process the results as needed
 */
async function getStoreLocationCentiroAction(
    input: GetStoreLocationCentiroInput,
    ctx: Msdyn365.IActionContext
): Promise<IFullOrgUnitAvailabilityExtended[]> {
    if (!input.Networks || !input.MaxDistance || !input.MaxCollectionPoints) {
        return [];
    }

    const deliveryAddressCentiro: ICollectionPoints = await bSGetCollectionPointsAsync(
        { callerContext: ctx, queryResultSettings: {} },
        input
    );

    if (deliveryAddressCentiro.hasError) {
        throw new Error(deliveryAddressCentiro.message);
    }

    const collectionPoints = deliveryAddressCentiro.collectionPoints;
    const mappedCollection: IFullOrgUnitAvailabilityExtended[] | undefined = collectionPoints?.map((collectionPoint: ICollectionPoint) => {
        const addressLines = [
            `${collectionPoint.street} ${collectionPoint.houseNumber}`,
            `${collectionPoint.city} ${collectionPoint.zipCode}`,
            collectionPoint.country,
            collectionPoint.address2
        ]
            .filter(line => line && line.trim().length > 0)
            .join(', ');
        return {
            OrgUnitAvailability: {
                OrgUnitLocation: {
                    ChannelId: ctx.requestContext.channel?.RecordId,
                    Latitude: collectionPoint.geoPoint?.latitude,
                    Longitude: collectionPoint.geoPoint?.longitude,
                    OrgUnitName: collectionPoint.name?.trim(),
                    OrgUnitNumber: collectionPoint.name?.trim(),
                    Address: addressLines,
                    Street: collectionPoint.street?.trim(),
                    StreetNumber: '',
                    City: collectionPoint.city?.trim(),
                    DistrictName: '',
                    BuildingCompliment: '',
                    Postbox: '',
                    Zip: collectionPoint.zipCode?.trim(),
                    County: '',
                    CountyName: '',
                    State: collectionPoint.state?.trim(),
                    StateName: collectionPoint.state?.trim(),
                    Country: collectionPoint.country?.trim(),
                    Distance: collectionPoint.distance,
                    InventoryLocationId: '',
                    InventorySiteId: '',
                    OpenFrom: 0,
                    OpenTo: 0,
                    ExternalId: collectionPoint.externalId
                }
            },
            openingHours: collectionPoint.openingHours
        };
    });
    return mappedCollection || [];
}

export default Msdyn365.createObservableDataAction({
    action: <Msdyn365.IAction<IFullOrgUnitAvailabilityExtended[]>>getStoreLocationCentiroAction,
    id: 'GetStoreLocationCentiro',
    input: createInput
});
